import api, { API } from "@/lib/api";
import { useIsAndroidApp } from "@/hooks/useLaunchContext";
import { canUseGooglePlayBilling, getLaunchContext } from "@/utils/pwaContext";
import { trackEvent } from "@/utils/analytics";

// Must match backend/play_billing.py SKU_TO_TOKENS and the managed products created in
// Google Play Console (package com.kalypsomusic.app) - same 5 tiers as the web token packs.
const SKU_BY_PACK = {
  5: "wallet_tokens_5", 10: "wallet_tokens_10", 20: "wallet_tokens_20", 50: "wallet_tokens_50",
  100: "wallet_tokens_100", 200: "wallet_tokens_200",
};

// Play Billing failures happen entirely on the user's own device - fire-and-forget so we
// finally get visibility into them server-side (see /api/payments/report-client-error),
// without ever blocking or breaking the purchase flow itself if the report call fails too.
// fetch + keepalive (17/08/2026 incident): axios XHR gets dropped when the TWA/WebView is
// killed or frozen right after a billing failure - production showed ZERO reports arriving
// for 4h despite 13 failed attempts. keepalive lets the browser finish the POST even if the
// page is being torn down, which is exactly when these reports matter most.
export function reportPaymentError(details) {
  // Contexte device systématique (RCA prod 15/08/2026 : impossible de distinguer app
  // installée vs navigateur, ni cause instantanée vs interaction, faute de ces champs).
  const payload = {
    launch_context: getLaunchContext(),
    user_agent: (typeof navigator !== "undefined" ? navigator.userAgent || "" : "").slice(0, 300),
    ...details,
  };
  try {
    const token = localStorage.getItem("ams_token");
    fetch(`${API}/payments/report-client-error`, {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch {
    api.post("/payments/report-client-error", payload).catch(() => {});
  }
  // GA4 visibility (Itération 49 audit) alongside the permanent DB trace above - lets the
  // funnel be explored live in Analytics without waiting for an admin dashboard query.
  trackEvent("payment_error", { provider: details.provider, step: details.step, error_name: details.error_name });
}

// (17/08/2026 production RCA - a REAL paid subscription was lost): request.show() stays
// pending the WHOLE time the buyer is inside the native Play sheet (picking a card,
// confirming - easily much longer than 7s), so racing it against a hard timeout aborted
// purchases MID-PAYMENT: Google charged, the purchaseToken resolved into an already-rejected
// race, verify/acknowledge never ran -> 3-day authorization hold, then auto-refund. Fix: the
// timeout now only fires when the sheet NEVER OPENED (detected via page visibility/blur - the
// native sheet pauses the TWA page, which the old hang-forever failure mode never does), and
// if a payment still completes after a timeout somehow, onLateResult captures the token anyway
// so the sale is saved server-side instead of silently lost.
export function showWithSheetTimeout(request, onLateResult) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timedOut = false;
    let sheetVisible = false;
    const markVisible = () => { sheetVisible = true; };
    document.addEventListener("visibilitychange", markVisible);
    window.addEventListener("blur", markVisible);
    const cleanup = () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", markVisible);
      window.removeEventListener("blur", markVisible);
    };
    const timer = setTimeout(() => {
      if (sheetVisible || settled) return; // the buyer is inside the native sheet - let them pay
      timedOut = true;
      settled = true;
      // Release the stale PaymentRequest UI slot - Chrome only allows ONE show() at a time,
      // so without this abort every SUBSEQUENT click would instantly reject with AbortError.
      try { request.abort().catch(() => {}); } catch { /* noop */ }
      cleanup();
      reject(Object.assign(new Error("Google Play Billing timed out"), { name: "TimeoutError" }));
    }, 7000);
    request.show().then(
      (response) => {
        if (timedOut) {
          onLateResult?.(response);
          return;
        }
        if (!settled) { settled = true; cleanup(); resolve(response); }
      },
      (err) => {
        if (!settled) { settled = true; cleanup(); reject(err); }
      },
    );
  });
}

function openGooglePlayPurchaseSheet(sku, pack, onLateResult) {
  const request = new window.PaymentRequest(
    [{ supportedMethods: "https://play.google.com/billing", data: { sku } }],
    { total: { label: `${pack} jetons Kalypso Music`, amount: { currency: "EUR", value: "0" } } },
  );
  return showWithSheetTimeout(request, onLateResult);
}

// Consecutive AbortError counter (17/08/2026 production RCA): the Play sheet opening then
// closing in error surfaces as AbortError - indistinguishable from a voluntary cancel. One
// abort = probably a real cancel (stay silent). Two in a row in the same session = almost
// certainly the billing channel is broken on that device (a real user retried 7x in 6 min
// with zero telemetry) - treat it as a genuine failure and reveal the Mollie card fallback.
let consecutivePlayAborts = 0;

// Bascule web automatique (15/08/2026, demande explicite admin : "ne plus perdre les ventes") :
// dès le 1er échec Google Play, on crée le paiement Mollie et on redirige DIRECTEMENT vers la
// page de paiement hébergée (carte + Google Pay + Apple Pay selon les méthodes activées côté
// Mollie), sans clic supplémentaire. Retourne null si la création échoue (l'appelant révèle
// alors le formulaire carte intégré via playBillingFallback).
async function autoWebFallback(pack, resumeOrderId, resumeAction, songPurchase) {
  try {
    const { data } = await api.post("/payments/create", {
      purpose: songPurchase ? "song_purchase" : "wallet_topup", token_pack: songPurchase ? null : pack,
      resume_order_id: resumeOrderId, resume_action: resumeAction,
    });
    if (data?.checkout_url) {
      trackEvent("mollie_checkout_redirect", { value: pack, currency: "EUR", reason: "play_billing_auto_fallback" });
      window.location.assign(data.checkout_url);
      return { ...data, redirected: true };
    }
  } catch { /* noop - embedded card form fallback below */ }
  return null;
}

async function purchaseViaGooglePlay(pack, { resumeOrderId, resumeAction, songPurchase } = {}) {
  if (!("getDigitalGoodsService" in window)) {
    throw new Error("Google Play Billing n'est pas disponible sur cet appareil.");
  }
  const sku = songPurchase ? "song_unlock_999" : SKU_BY_PACK[pack];
  if (!sku) throw new Error("Pack de jetons invalide.");
  trackEvent("begin_checkout", { value: pack, currency: "EUR", method: "google_play" });

  // CRITICAL (14/08/2026 incident review - real production client_payment_errors): request.show()
  // must run inside the click's short-lived "transient user activation" window, or Chrome
  // rejects it outright with `SecurityError: PaymentRequest.show() requires either transient
  // user activation...`. That's exactly what several real customers hit repeatedly (same
  // user/device failing 4 times in a row) - caused by awaiting the backend payment-record
  // creation call BEFORE opening the purchase sheet: on a slower device/network that network
  // round-trip alone is enough to burn through the activation window. Fix: call
  // openGooglePlayPurchaseSheet() FIRST, with zero await before it, and create the backend
  // record concurrently instead of sequentially in front of it.
  const shownAt = Date.now();
  const purchaseSheetPromise = openGooglePlayPurchaseSheet(sku, pack, async (lateResponse) => {
    // The buyer completed the payment AFTER our timeout had already given up (e.g. the
    // visibility signal was missed on an exotic device) - capture the token and verify it
    // server-side anyway so the sale is credited instead of refunded by Google in 3 days.
    try {
      const token = lateResponse.details?.purchaseToken;
      await lateResponse.complete("success");
      const { data: pending } = await pendingPromise;
      if (token) {
        await api.post("/payments/google-play/verify", { payment_id: pending.payment_id, purchase_token: token });
        reportPaymentError({
          payment_id: pending.payment_id, provider: "google_play", step: "late_capture",
          error_name: "LateSuccess", error_message: "purchase completed after timeout - token captured and verified",
        });
      }
    } catch { /* noop - already reported via the timeout path */ }
  });
  const pendingPromise = api.post("/payments/create", {
    purpose: songPurchase ? "song_purchase" : "wallet_topup", token_pack: songPurchase ? null : pack, platform: "android",
    resume_order_id: resumeOrderId, resume_action: resumeAction,
  });

  let response;
  try {
    response = await purchaseSheetPromise;
  } catch (err) {
    const pendingResult = await pendingPromise.catch(() => null);
    const elapsedMs = Date.now() - shownAt;
    if (err?.name === "AbortError") {
      // The native sheet closed - user cancel OR Google Play error, indistinguishable per
      // Chromium docs. Since 15/08/2026 (admin decision after repeated lost sales): the very
      // FIRST abort switches straight to the hosted web payment (Mollie/Google Pay/Apple Pay).
      consecutivePlayAborts += 1;
      trackEvent("checkout_abandoned", { value: pack, currency: "EUR", method: "google_play", reason: "user_cancelled" });
      reportPaymentError({
        payment_id: pendingResult?.data?.payment_id, provider: "google_play", step: "request.show",
        error_name: "AbortError", error_message: `${err?.message || "sheet dismissed"} (consecutive #${consecutivePlayAborts})`,
        elapsed_ms: elapsedMs, sku,
      });
      // Règle admin 18/08/2026 : la bascule web ne se fait qu'au 2e échec consécutif (passer
      // au web dès la 1ère annulation faisait bizarre). Exception conservée : fermeture < 3s
      // = panne technique certaine (le client n'a pas pu interagir) -> bascule immédiate.
      // 1er échec normal -> toast visible "Paiement annulé, réessayez" (isPurchaseCancelled).
      if (elapsedMs < 3000 || consecutivePlayAborts >= 2) {
        const web = await autoWebFallback(pack, resumeOrderId, resumeAction, songPurchase);
        if (web) return web;
        const fallbackErr = new Error("Le paiement Google Play a échoué.");
        fallbackErr.playBillingFallback = true;
        throw fallbackErr;
      }
      const cancelled = new Error("cancelled");
      cancelled.isPurchaseCancelled = true;
      throw cancelled;
    }
    // NOTE: no automatic retry here anymore. A retry would call request.show() again OUTSIDE
    // the original click's activation window, which per spec is guaranteed to fail identically
    // for SecurityError (confirmed by the 4 repeated real failures above) - only a genuinely
    // NEW click gives a fresh activation window, so we fail fast and ask for exactly that.
    reportPaymentError({
      payment_id: pendingResult?.data?.payment_id, provider: "google_play", step: "request.show",
      error_name: err?.name, error_message: err?.message, elapsed_ms: elapsedMs, sku,
    });
    // 1er échec technique (SecurityError, Timeout...) = redirection directe vers le paiement
    // web hébergé; si la création Mollie échoue, l'appelant révèle le formulaire carte intégré.
    const web = await autoWebFallback(pack, resumeOrderId, resumeAction, songPurchase);
    if (web) return web;
    // Fallback (16/08/2026 incident review): Google Play's alternative-billing policy allows
    // offering another payment method inside the app once native billing genuinely fails
    // (confirmed via policy research) - tag the error instead of just failing outright, so the
    // UI (Checkout.jsx, WalletTab.jsx, OrderStatus.jsx) can seamlessly reveal the embedded
    // Mollie card form and save the sale instead of showing a dead-end error toast.
    const fallbackErr = new Error(
      err?.name === "SecurityError"
        ? "Le paiement Google Play n'a pas pu s'ouvrir."
        : "Le paiement Google Play a échoué."
    );
    fallbackErr.playBillingFallback = true;
    throw fallbackErr;
  }

  consecutivePlayAborts = 0;

  // The purchase sheet resolved successfully, so the backend record creation (kicked off in
  // parallel above) has had plenty of time to finish too - await it now for its payment_id.
  const { data: pending } = await pendingPromise;

  // 3) The purchase token is delivered directly in the payment response itself
  // (response.details.purchaseToken) - calling DigitalGoodsService.listPurchases()
  // afterwards is unreliable here (the service binding can already be torn down by then,
  // surfacing as a "clientAppUnavailable" error) and is not needed.
  const purchaseToken = response.details?.purchaseToken;
  await response.complete("success");
  if (!purchaseToken) {
    reportPaymentError({ payment_id: pending.payment_id, provider: "google_play", step: "purchaseToken", error_message: "missing purchaseToken in response.details" });
    throw new Error("Jeton d'achat introuvable dans la réponse Google Play, veuillez réessayer.");
  }
  // The native purchase sheet resolved with a real token - the visitor actively went through
  // with the payment (not a cancellation), even if server-side verification below still fails.
  trackEvent("add_payment_info", { value: pack, currency: "EUR", method: "google_play" });

  // 4) Server-side verification + acknowledgment (mandatory, see backend/play_billing.py) -
  // tokens are only credited by the backend AFTER Google confirms the purchase is genuine,
  // never client-side, which is what avoids Google's 3-day fund hold on unacknowledged
  // purchases and protects against fraud.
  try {
    const { data: result } = await api.post("/payments/google-play/verify", {
      payment_id: pending.payment_id, purchase_token: purchaseToken,
    });
    return result; // { status: "paid" } on success
  } catch (err) {
    reportPaymentError({
      payment_id: pending.payment_id, provider: "google_play", step: "verify",
      error_name: err?.name, error_message: err?.response?.data?.detail || err?.message,
    });
    throw err;
  }
}

/**
 * Single entry point used by every "buy a token pack" UI (WalletTab, Checkout). Routes to
 * Google Play Billing when running inside the Android app (TWA), or to the existing web flow
 * (Mollie / simulated) otherwise - see utils/pwaContext.js for how the context is detected.
 * Google Play policy forbids offering Mollie for digital goods inside the Play-distributed
 * app, so the two paths never coexist for the same launch context.
 */
export function useTokenPurchase() {
  const isAndroidApp = useIsAndroidApp();

  const buyPack = async (pack, opts = {}) => {
    // Route by actual Google Play Billing availability (Digital Goods API), NOT by the
    // `document.referrer`-based `isAndroidApp` flag - see pwaContext.js for why the latter can
    // be unreliable across TWA/Chrome builds while the referrer-based check is still fine for
    // low-stakes UI decisions elsewhere (footer, mailto links, etc.).
    if (await canUseGooglePlayBilling()) return purchaseViaGooglePlay(pack, opts);
    const { data } = await api.post("/payments/create", {
      purpose: opts.songPurchase ? "song_purchase" : "wallet_topup", token_pack: opts.songPurchase ? null : pack,
      resume_order_id: opts.resumeOrderId, resume_action: opts.resumeAction,
    });
    // Funnel visibility (Itération 39 audit): fires right when a real Mollie checkout is
    // about to open, so GA4 can measure how many "checkout initiated" ever reach "purchase"
    // (see index.html gtag config) - purely observational, never blocks/alters the redirect.
    if (data.checkout_url) {
      trackEvent("mollie_checkout_redirect", { value: pack, currency: "EUR" });
    }
    return data;
  };

  // Embedded Mollie Components card flow (see MollieCardPayment.jsx) - the card was already
  // tokenized client-side, so the payment either completes immediately (status: "paid") or,
  // rarely, still needs a checkout_url redirect for a 3-D Secure challenge.
  const buyPackWithCard = async (pack, cardToken, opts = {}) => {
    const { data } = await api.post("/payments/create", {
      purpose: opts.songPurchase ? "song_purchase" : "wallet_topup", token_pack: opts.songPurchase ? null : pack, card_token: cardToken,
      resume_order_id: opts.resumeOrderId, resume_action: opts.resumeAction,
    });
    return data;
  };

  // Apple Pay - `method` here is the wallet the visitor tapped in our UI ("applepay" is
  // currently the only one MollieCardPayment.jsx ever sends - Google Pay is deliberately
  // hidden, see MollieCardPayment.jsx). Apple Pay is a real Mollie method: its hosted page
  // shows ONLY that wallet button, no card fields, no confusion.
  const buyPackWithMethod = async (pack, method, opts = {}) => {
    const { data } = await api.post("/payments/create", {
      purpose: opts.songPurchase ? "song_purchase" : "wallet_topup", token_pack: opts.songPurchase ? null : pack, method,
      resume_order_id: opts.resumeOrderId, resume_action: opts.resumeAction,
    });
    return data;
  };

  return { buyPack, buyPackWithCard, buyPackWithMethod, isAndroidApp };
}
